import React, {
  useRef, useState, useEffect, useMemo,
} from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { useQueryClient } from '@tanstack/react-query';
import S from './RecordAudioCard';
import { Button } from '../../../../components/ui/Button/Button';
import Input from '../../../../components/common/Input/Input';
import { uploadAudioAnnouncementFiles } from '../../../../API/services/AudioAnnouncementService';
import { showUploadResult, toast } from '../../../../utils/toast';

const ffmpeg = new FFmpeg();

const RECORDING_LIMIT = 5 * 1024 * 1024;

function MicIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 14a3 3 0 003-3V5a3 3 0 10-6 0v6a3 3 0 003 3z" />
      <path d="M19 11a7 7 0 01-14 0H3a9 9 0 0018 0h-2z" />
      <path d="M12 20v-3" />
    </svg>
  );
}

function StopIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  );
}

function UploadIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 15V5" />
      <path d="M8 9l4-4 4 4" />
      <path d="M4 19h16" />
    </svg>
  );
}

function DownloadIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v10" />
      <path d="M8 11l4 4 4-4" />
      <path d="M4 19h16" />
    </svg>
  );
}

function WarningIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L1 21h22L12 2zm0 14h-1v-4h2v4h-1zm0 4h-1v-2h2v2h-1z" />
    </svg>
  );
}

export default function RecordAudioModalContent({ audioList, fleetId, onClose }: any): JSX.Element {
  const [status, setStatus] = useState('idle');
  const [duration, setDuration] = useState(0);
  const [recordedSize, setRecordedSize] = useState(0);
  const [audioFileName, setAudioFileName] = useState('');
  const [type, setType] = useState<'mp3' | 'aac'>('aac');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [strLen, setStrLen] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const durationRef = useRef(0);

  const isValidName = audioFileName.trim().length >= 3;
  const queryClient = useQueryClient();
  /* TIMER */
  useEffect(() => {
    if (status === 'recording') {
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          const newVal = prev + 1;
          durationRef.current = newVal;
          return newVal;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.load();
    }
  }, [audioUrl]);

  useEffect(
    () => () => {
      // stop recorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // stop microphone stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    },
    [],
  );

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isDuplicateName = (): boolean => {
    if (!audioList || !audioFileName) return false;

    const newFileName = `${audioFileName}.${type}`.toLowerCase();

    return audioList.some(
      (audio: any) => audio.name.toLowerCase() === newFileName,
    );
  };

  /* STOP RECORDING */

  const stopRecording = (): void => {
    // ✅ STOP TIMER IMMEDIATELY
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    mediaRecorderRef.current?.stop();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    setStatus('stopped');
  };

  /* START RECORDING */
  const startRecording = async (): Promise<void> => {
    if (mediaRecorderRef.current?.state === 'recording') return;
    try {
      setErrorMessage(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      setRecordedSize(0);
      setDuration(0);
      setAudioUrl(null);

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);

          setRecordedSize((prev) => {
            const newSize = prev + event.data.size;
            const TARGET_SIZE = 4.99 * 1024 * 1024; // bytes
            if (newSize >= TARGET_SIZE) {
              stopRecording();
              setErrorMessage(
                'Recording stopped: Maximum audio size of 5 MB reached',
              );
            }

            return newSize;
          });
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });

        if (!ffmpeg.loaded) {
          await ffmpeg.load();
        }

        await ffmpeg.writeFile('input.webm', await fetchFile(blob));

        await ffmpeg.exec([
          '-i',
          'input.webm',
          '-c:a',
          'pcm_s16le',
          'output.wav',
        ]);

        const data = await ffmpeg.readFile('output.wav');

        const wavBlob = new Blob([data], { type: 'audio/wav' });

        const url = URL.createObjectURL(wavBlob);

        setAudioUrl(url);
        const audio = new Audio(url);
        audio.onloadedmetadata = () => {
          const realDuration = Math.floor(audio.duration);
          setDuration(realDuration);
          durationRef.current = realDuration;
        };
      };

      mediaRecorder.start(500);
      setStatus('recording');
    } catch {
      setErrorMessage('Microphone access denied or unavailable.');
    }
  };

  /* INPUT */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    if (value.length <= 30) {
      setAudioFileName(value);
    }
  };

  /* LOAD FFMPEG */

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!ffmpeg.loaded) {
        await ffmpeg.load();
      }
    };

    load();
  }, []);

  // useEffect(() => {
  //   if (audioRef.current) {
  //     audioRef.current.onloadedmetadata = () => {
  //       setDuration(Math.floor(audioRef.current!.duration));
  //     };
  //   }
  // }, [audioUrl]);

  /* AUDIO CONVERSION */

  const convertAudio = async (): Promise<Blob> => {
    if (!audioUrl) throw new Error('No audio available');

    if (!ffmpeg.loaded) {
      await ffmpeg.load();
    }

    const response = await fetch(audioUrl);
    const blob = await response.blob();

    try {
      await ffmpeg.deleteFile('input.webm');
    } catch (e) {
      // file may not exist
    }

    try {
      await ffmpeg.deleteFile('output.mp3');
    } catch (e) {
      // file may not exist
    }

    try {
      await ffmpeg.deleteFile('output.aac');
    } catch (e) {
      // file may not exist
    }

    await ffmpeg.writeFile('input.webm', await fetchFile(blob));

    const outputFile = type === 'aac' ? 'output.aac' : 'output.mp3';
    const seconds = durationRef.current;
    const safeSeconds = Math.max(0, seconds - 0.3);
    if (type === 'aac') {
      await ffmpeg.exec([
        '-fflags', '+genpts',
        '-i', 'input.webm',
        '-t', `${safeSeconds}`,
        '-af', 'asetpts=PTS-STARTPTS',
        '-avoid_negative_ts', 'make_zero',
        '-c:a', 'aac',
        '-ar', '44100',
        '-ac', '1',
        '-b:a', '112k',
        outputFile,
      ]);
    } else {
      await ffmpeg.exec([
        '-i',
        'input.webm',
        '-t',
        `${seconds}`,
        '-vn',
        '-ar',
        '44100',
        '-ac',
        '1',
        '-b:a',
        '112k',
        outputFile,
      ]);
    }

    const data = await ffmpeg.readFile(outputFile);

    const outputBlob = new Blob(
      [data],
      { type: type === 'mp3' ? 'audio/mpeg' : 'audio/aac' },
    );

    return outputBlob;
  };
  console.log(audioList, 'audio');

  /* UPLOAD */

  const uploadAudio = async (): Promise<void> => {
    setIsUploading(true);
    try {
      setErrorMessage(null);
      setDuplicateError(false);

      if (isDuplicateName()) {
        setDuplicateError(true);
        setIsUploading(false);
        return;
      }

      const audioBlob = await convertAudio();

      const file = new File(
        [audioBlob],
        `${audioFileName}.${type}`,
        { type: audioBlob.type },
      );
      /* eslint-disable object-shorthand */
      const response = await uploadAudioAnnouncementFiles({
        fleetId: fleetId,
        files: [file],
      });
      /* eslint-enable object-shorthand */
      showUploadResult(response);

      if (response.failedFiles?.length) {
        setErrorMessage('Some files failed to upload.');
      } else {
        onClose();
        await queryClient.invalidateQueries({
          queryKey: ['audio-announcement', fleetId],
        });
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Upload limit reached already!.');
    } finally {
      setIsUploading(false);
    }
  };

  /* DOWNLOAD */

  const downloadAudio = async (): Promise<void> => {
    try {
      const audioBlob = await convertAudio();

      const url = URL.createObjectURL(audioBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${audioFileName || 'audio'}.${type}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setErrorMessage('Download failed.');
    }
  };

  const audioPlayer = useMemo(() => {
    if (!audioUrl) return null;

    return (
      /* eslint-disable-next-line jsx-a11y/media-has-caption */
      <audio
        key={audioUrl}
        ref={audioRef}
        controls
        controlsList="nodownload noplaybackrate"
        preload="auto"
        src={audioUrl}
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
        }}
        style={{
          width: '100%',
          height: '40px',
          borderRadius: '8px',
          background: '#f0f4ff',
        }}
      />
    );
  }, [audioUrl]);

  const handleBlur = (e: any): void => {
    if (e.target.value.length < 3) {
      setStrLen(true);
    } else {
      setStrLen(false);
    }
  };

  const handleFocus = (): void => {
    setStrLen(false);
    setDuplicateError(false);
  };

  return (
    <S.ModalBody>

      <S.StatusContainer>

        <S.StatusCard>
          <S.StatusLabel>Status</S.StatusLabel>
          <S.StatusValue>
            {status === 'recording' ? '🔴 Recording' : status}
          </S.StatusValue>
        </S.StatusCard>

        <S.StatusCard>
          <S.StatusLabel>Duration</S.StatusLabel>
          <S.StatusValue>{formatTime(duration)}</S.StatusValue>
        </S.StatusCard>

        <S.StatusCard>
          <S.StatusLabel>Size</S.StatusLabel>
          <S.StatusValue>
            {(recordedSize / 1024 / 1024).toFixed(2)} MB
          </S.StatusValue>
        </S.StatusCard>

      </S.StatusContainer>

      {errorMessage && (
        <p style={{ color: 'red', marginTop: '10px' }}>
          {errorMessage}
        </p>
      )}

      <S.Section>
        <Input
          label="Audio File name"
          name="audiofileName"
          placeholder="Audio File name"
          value={audioFileName}
          onChange={handleInputChange}
          disabled={status === 'recording'}
          maxLength={30}
          onBlur={(e: any) => handleBlur(e)}
          onFocus={(e: any) => handleFocus()}
          error={strLen || duplicateError}
        />

        {(strLen || duplicateError) && (
          <S.ErrorWrapper>
            <S.ErrorMessage>
              <WarningIcon />
              {strLen
                ? 'Audio file name must be at least 3 characters'
                : `Audio file ${audioFileName}.${type} already exists`}
            </S.ErrorMessage>
          </S.ErrorWrapper>
        )}
      </S.Section>
      <S.Status>
        <S.Label>Audio Type:</S.Label>

        <S.AudioTypeContainer>

          <S.HiddenRadio
            id="audioAAC"
            type="radio"
            name="audioType"
            checked={type === 'aac'}
            onChange={() => setType('aac')}
          />

          <S.AudioTypeOption htmlFor="audioAAC" active={type === 'aac'}>
            AAC
          </S.AudioTypeOption>

          <S.HiddenRadio
            id="audioMP3"
            type="radio"
            name="audioType"
            checked={type === 'mp3'}
            onChange={() => setType('mp3')}
          />

          <S.AudioTypeOption htmlFor="audioMP3" active={type === 'mp3'}>
            MP3
          </S.AudioTypeOption>

        </S.AudioTypeContainer>
      </S.Status>

      <S.RecordControls>
        <S.ModernButton
          disabled={!isValidName || status === 'recording'}
          onClick={startRecording}
          style={{ background: '#4CAF50', color: '#fff' }}
        >
          <MicIcon />
          Start Recording
        </S.ModernButton>

        <S.ModernButton
          disabled={status !== 'recording'}
          onClick={stopRecording}
          style={{ background: '#ff4d4f', color: '#fff' }}
        >
          <StopIcon />
          Stop Recording
        </S.ModernButton>
      </S.RecordControls>

      {audioUrl && (
        <S.Section>

          <S.PreviewCard>

            <S.PreviewHeader>
              🎧 Audio Preview
            </S.PreviewHeader>

            <S.PlayerWrapper>
              {audioPlayer}
            </S.PlayerWrapper>

            <S.PreviewActions>

              <S.PrimaryButton onClick={uploadAudio} disabled={isUploading}>
                <UploadIcon />
                {isUploading ? 'Uploading...' : 'Upload'}
              </S.PrimaryButton>

              <S.SecondaryButton onClick={downloadAudio}>
                <DownloadIcon />
                Download
              </S.SecondaryButton>

            </S.PreviewActions>

          </S.PreviewCard>

        </S.Section>
      )}

    </S.ModalBody>
  );
}
